import * as Sentry from '@sentry/browser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIndexedDB } from 'react-indexed-db-hook';
import SignaturePad from 'signature_pad';
import { remove, uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { LOCAL_STORAGE_KEYS, S3_IMAGE_PATH } from '../../utils/constants';
import { base64ToBlob, resolveActiveCompanyId } from '../../utils/helperFunctions';
import { useUserContext } from '../../contexts/UserContext';
import {
    getSavedSignatureLocalStorageKey,
    readSavedSignatureData,
    toSignatureError,
} from './inspectionSignatureUtils';

type UseInspectionSignatureOptions = {
    companyId?: string;
};

export function useInspectionSignature({ companyId: companyIdOverride }: UseInspectionSignatureOptions = {}) {
    const { company, companyOwner, companyUser } = useUserContext();
    const { update, getByID, deleteRecord } = useIndexedDB('signature');
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [signaturePad, setSignaturePad] = useState<SignaturePad | null>(null);
    const [signatureCaptured, setSignatureCaptured] = useState(false);
    const [hasDrawing, setHasDrawing] = useState(false);

    const companyId =
        companyIdOverride ||
        resolveActiveCompanyId({ companyUser, companyOwner, company }) ||
        undefined;
    const equipmentId = localStorage.getItem(LOCAL_STORAGE_KEYS.INSPECT);
    const unitId = localStorage.getItem(LOCAL_STORAGE_KEYS.UNIT_ID);
    const equipmentName = localStorage.getItem(LOCAL_STORAGE_KEYS.EQUIPMENT_NAME);
    const savedSignatureKey = companyId ? getSavedSignatureLocalStorageKey(companyId) : '';

    const persistSignatureRecord = useCallback(
        async (signatureData: string) => {
            if (!companyId) {
                return;
            }

            localStorage.setItem(savedSignatureKey, signatureData);
            // Use update() (IndexedDB put, upsert) rather than add(): the record is
            // keyed on companyId, so re-signing or saving a second inspection on the
            // same device would make add() throw ConstraintError ("Key already
            // exists"). put() overwrites the existing record with the new signature.
            await update({
                id: companyId,
                signatureData,
                equipmentId,
                unitId,
                equipmentName,
            });
        },
        [update, companyId, equipmentId, equipmentName, savedSignatureKey, unitId],
    );

    useEffect(() => {
        if (!canvasRef.current) {
            return;
        }

        const canvas = canvasRef.current;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext('2d')?.scale(ratio, ratio);

        const pad = new SignaturePad(canvas, {
            minWidth: 1,
            maxWidth: 2.5,
            penColor: 'rgb(30, 58, 138)',
            backgroundColor: 'rgb(255, 255, 255)',
        });

        const syncDrawingState = () => {
            setHasDrawing(!pad.isEmpty());
        };

        pad.addEventListener('beginStroke', syncDrawingState);
        pad.addEventListener('endStroke', syncDrawingState);

        setSignaturePad(pad);

        const savedSignature = readSavedSignatureData(companyId);
        if (savedSignature) {
            pad.fromDataURL(savedSignature);
            setSignatureCaptured(true);
            setHasDrawing(true);

            if (companyId) {
                void (async () => {
                    try {
                        const existing = await getByID(companyId);
                        if (existing?.signatureData) {
                            return;
                        }

                        await persistSignatureRecord(savedSignature);
                    } catch (error) {
                        console.error('Error syncing stored signature to IndexedDB:', error);
                        Sentry.captureException(toSignatureError(error, 'Error syncing stored signature to IndexedDB'));
                    }
                })();
            }
        }

        return () => {
            pad.off();
        };
    }, [companyId, getByID, persistSignatureRecord, savedSignatureKey]);

    useEffect(() => {
        const loadStoredSignature = async () => {
            if (!signaturePad || !companyId) {
                return;
            }

            try {
                const storedSignature = await getByID(companyId);
                if (!storedSignature) {
                    return;
                }

                const signatureData =
                    typeof storedSignature.signatureData === 'string'
                        ? storedSignature.signatureData
                        : null;

                if (signatureData) {
                    signaturePad.fromDataURL(signatureData);
                    setSignatureCaptured(true);
                    setHasDrawing(true);
                    return;
                }

                if (storedSignature.signatureBlob) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        signaturePad.fromDataURL(reader.result as string);
                        setSignatureCaptured(true);
                        setHasDrawing(true);
                    };
                    reader.readAsDataURL(storedSignature.signatureBlob);
                }
            } catch (error) {
                console.error('Error loading stored signature:', error);
                Sentry.captureException(toSignatureError(error, 'Error loading stored signature'));
            }
        };

        void loadStoredSignature();
    }, [signaturePad, companyId, getByID]);

    const clearSignature = async () => {
        signaturePad?.clear();
        setSignatureCaptured(false);
        setHasDrawing(false);

        try {
            await deleteRecord(companyId ?? '');

            const storedSignature = localStorage.getItem(LOCAL_STORAGE_KEYS.SIGNATURE_PATH);
            if (storedSignature) {
                try {
                    await remove({ path: storedSignature });
                } catch (error) {
                    console.error('Error removing signature from storage:', error);
                    Sentry.captureException(toSignatureError(error, 'Error removing signature from storage'));
                } finally {
                    localStorage.removeItem(LOCAL_STORAGE_KEYS.SIGNATURE_PATH);
                }
            }
        } catch (error) {
            console.error('Error clearing signature:', error);
            Sentry.captureException(toSignatureError(error, 'Error clearing signature'));
        }
    };

    const saveSignature = async () => {
        if (!signaturePad || signaturePad.isEmpty() || !companyId) {
            return;
        }

        try {
            const signatureData = signaturePad.toDataURL('image/jpeg', 0.8);
            const blob = await base64ToBlob(signatureData, 'image/jpeg');

            // persistSignatureRecord writes the signature to localStorage *before*
            // the IndexedDB add, so an IndexedDB failure (common on iOS Safari —
            // Private Browsing, quota, transient UnknownError) does not lose the
            // signature. Treat it as non-fatal: report it, but still upload to S3
            // and keep the signature captured.
            try {
                await persistSignatureRecord(signatureData);
            } catch (persistError) {
                Sentry.captureException(toSignatureError(persistError, 'Error saving signature to IndexedDB'));
            }

            if (navigator.onLine) {
                const imageName = `${equipmentId}/${unitId ? `${unitId}/` : ''}${uuidv4()}-SIGNATURE-${equipmentName}.jpeg`;
                const imageFile = new File([blob!], imageName, { type: 'image/jpeg' });

                const savedImageS3 = await uploadData({
                    path: S3_IMAGE_PATH.signatureImagePath(companyId, imageName),
                    data: imageFile,
                }).result;

                localStorage.setItem(LOCAL_STORAGE_KEYS.SIGNATURE_PATH, savedImageS3.path);
            }

            setSignatureCaptured(true);
        } catch (error) {
            console.error('Error saving signature:', error);
            setSignatureCaptured(false);
            Sentry.captureException(toSignatureError(error, 'Error saving signature'));
        }
    };

    const ensureSignaturePersisted = async (): Promise<boolean> => {
        if (!companyId) {
            return false;
        }

        if (signaturePad && !signaturePad.isEmpty()) {
            await saveSignature();
            return true;
        }

        const savedSignature = readSavedSignatureData(companyId);
        if (!savedSignature) {
            return false;
        }

        try {
            await persistSignatureRecord(savedSignature);
            setSignatureCaptured(true);
            setHasDrawing(true);
            return true;
        } catch (error) {
            console.error('Error persisting saved signature:', error);
            Sentry.captureException(toSignatureError(error, 'Error persisting saved signature'));
            return false;
        }
    };

    return {
        canvasRef,
        signatureCaptured,
        hasDrawing,
        clearSignature,
        saveSignature,
        ensureSignaturePersisted,
    };
}
